// @flow
import { Box } from 'grommet';
import { isEmpty } from 'lodash';
import * as React from 'react';
import * as Yup from 'yup';
import {
    injectIntl, defineMessages,
} from 'react-intl';
import RichTextEditor from 'react-rte';
import FieldWrapper from '../FieldWrapper';

type Props = {
    field: {
        onChange: (e: SyntheticInputEvent<number>) => void,
        onFocus: (e: SyntheticFocusEvent<HTMLInputElement>) => void,
        onBlur: (e: SyntheticFocusEvent<any>) => void,
        value: any;
        name: string;
    },
    form: {
        errors: Object,
        touched: Object,
        isSubmitting: boolean,
        setFieldValue: Function,
        setFieldTouched: Function,
    },
    required: boolean,
    label: string,
    help_text: string,
    disabled?: boolean,
}

const editorLeftTrimRegex = new RegExp('^(<p>(\\s+|(&nbsp;)|(<br>))*<\\/p>|\\s+)*', 'g');

const RichTextEditorInput = (props: Props): React.Node => {
    const {
        disabled,
        field: {
            name, onBlur, value,
        },
        form: {
            isSubmitting, setFieldValue, setFieldTouched,
        },
        label,
        help_text: help,
        required,
    } = props;

    const [editorValue, setEditorValue] = React.useState(value);

    const handleChange = (v) => {
        setEditorValue(v);
        const htmlText = v.toString('html');
        const trimmedText = htmlText.replace(editorLeftTrimRegex, '');

        if (isEmpty(trimmedText)) {
            setFieldValue(name, null);
        } else {
            setFieldValue(name, htmlText);
        }
    };

    const handleBlur = (e) => {
        setFieldTouched(name, true);
        onBlur(e);
    };

    return (
        <FieldWrapper
            name={name}
            required={required}
            helpText={help}
            label={label}
            placeholder=""
        >
            <Box height="400px">
                <RichTextEditor
                    editorClassName="rte-editor"
                    name={name}
                    onChange={handleChange}
                    placeholder=""
                    onBlur={handleBlur}
                    value={editorValue || RichTextEditor.createEmptyValue()}
                    id={`id-${name}`}
                    disabled={disabled || isSubmitting}
                    autoFocus
                    rootStyle={{
                        overflow: 'hidden',
                        width: '100%',
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 0,
                        borderLeft: 0,
                        borderRight: 0,
                    }}
                    editorStyle={{
                        overflow: 'auto',
                        height: '100%',
                    }}
                    toolbarStyle={{
                        paddingBottom: 0,
                    }}
                />
            </Box>
        </FieldWrapper>
    );
};

const messages = defineMessages({
    min: {
        id: 'rte-min',
        defaultMessage: 'The length of the text must be more than {minLimit} characters!',
    },
    max: {
        id: 'rte-max',
        defaultMessage: 'The length of the text must be less than {maxLimit} characters!',
    },
    required: {
        id: 'rte-required',
        defaultMessage: 'This field is required.',
    },
});

RichTextEditorInput.getValidationSchema = (field: { validation: Object }) => {
    let errMsg = Yup.string().nullable();
    if (field.validation.required) {
        errMsg = Yup.string().required(messages.required).nullable();
    }
    if (field.validation.min_length) {
        errMsg = errMsg.min(field.validation.min_length.toString(),
            { ...messages.min, values: { minLimit: field.validation.min_length.toString() } });
    }
    if (field.validation.max_length) {
        errMsg = errMsg.max(field.validation.max_length.toString(),
            { ...messages.max, values: { maxLimit: field.validation.max_length.toString() } });
    }

    return errMsg;
};

RichTextEditorInput.defaultProps = {
    disabled: false,
};

export default injectIntl(RichTextEditorInput);
